import React, { useState, useEffect } from 'react';
import { Clock, Gem, Sparkles, Building, Store, Award, ChevronRight } from 'lucide-react';

const TransformationJourney = () => {
  const [activeYear, setActiveYear] = useState('1941');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const timelineData = {
    '1941': {
      year: '1941',
      title: 'The Humble Beginning',
      description: 'Our journey started in 1941. This is how the shop looked at the very beginning, built on the pillars of trust and quality that still define us today.',
      images: [
        'image1.png'
      ],
      imageCaptions: ['DP Jewellers 1941', 'DP Jewellers 1941 Interior'],
      icon: Store
    },
    '1988': {
      year: '1988',
      title: 'The First Transformation',
      description: 'As the years passed, our commitment to excellence grew. In 1988, the shop underwent a significant transformation, embracing new styles while keeping our traditional values intact.',
      images: [
        'image1.png',
        'image5.png'
      ],
      imageCaptions: ['DP Jewellers 1988', 'DP Jewellers 1988 Evolution'],
      icon: Building
    },
    '1999': {
      year: '1999',
      title: 'Next Stage of Development',
      description: 'By 1999, DP Jewellers had become a household name. This stage of development saw us expanding our collections and modernization of our facilities to better serve our growing family of customers.',
      images: [
        'image2.png',
        'image4.png'
      ],
      imageCaptions: ['DP Jewellers 1999', 'DP Jewellers 1999 Expansion'],
      icon: Gem
    },
    '2024': {
      year: '2024',
      title: 'A New Era of Luxury',
      description: 'In 2024, we proudly launched our modernized flagship store, marking a full transformation into a modern masterpiece. Standing as a renowned beacon of luxury and exquisite craftsmanship, every single detail is meticulously crafted to perfection to serve you better and create an unforgettable immersive experience for our patrons.',
      images: [
        'image3.png',
        'image2.png'
      ],
      imageCaptions: ['DP Jewellers 2024 Storefront', 'DP Jewellers 2024 Current Look'],
      icon: Award
    }
  };

  const currentData = timelineData[activeYear];
  const CurrentIcon = currentData.icon;

  return (
    <section className="py-20 bg-gradient-to-b from-gray-900 to-gray-800 text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-16">
          <div className="flex justify-center mb-4">
            <Clock className="text-[#c49a6c]" size={40} />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">The Transformation Journey</h2>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto">
            Witness the legacy of DP Jewellers from its humble beginnings to its modern-day excellence.
          </p>
          <div className="w-24 h-1 bg-[#c49a6c] mx-auto mt-6 rounded-full"></div>
        </div>
        <div className="relative mb-16">
          <div className="absolute left-0 right-0 top-1/2 transform -translate-y-1/2 h-1 bg-gray-700 hidden lg:block"></div>
          
          <div className="relative flex flex-col lg:flex-row justify-between gap-6">
            {Object.keys(timelineData).map((year) => {
              const data = timelineData[year];
              const Icon = data.icon;
              const isActive = activeYear === year;
              
              return (
                <button
                  key={year}
                  onClick={() => setActiveYear(year)}
                  className={`relative flex-1 p-6 rounded-xl transition-all duration-300 ${
                    isActive 
                      ? 'bg-[#846358] shadow-xl transform scale-105' 
                      : 'bg-gray-800 hover:bg-gray-750'
                  }`}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className={`p-3 rounded-full mb-3 transition-all duration-300 ${
                      isActive ? 'bg-white/20' : 'bg-gray-700'
                    }`}>
                      <Icon size={24} className={isActive ? 'text-white' : 'text-gray-400'} />
                    </div>
                    <span className={`text-2xl font-bold mb-1 ${isActive ? 'text-white' : 'text-gray-300'}`}>
                      {year}
                    </span>
                    <span className={`text-sm ${isActive ? 'text-white/90' : 'text-gray-500'}`}>
                      {data.title.split(' ')[0]} {data.title.split(' ')[1]}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="bg-gray-800/50 rounded-2xl overflow-hidden backdrop-blur-sm">
            <div className="bg-gradient-to-r from-[#846358] to-[#a07a60] px-8 py-6">
              <div className="flex items-center gap-3">
                <CurrentIcon size={32} className="text-white" />
                <div>
                  <span className="text-3xl font-bold text-white">{currentData.year}</span>
                  <h3 className="text-xl font-semibold text-white/90 mt-1">{currentData.title}</h3>
                </div>
              </div>
            </div>
            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {currentData.images.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image}
                          alt={currentData.imageCaptions[index]}
                          className="w-full h-48 object-cover rounded-lg shadow-lg transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex items-center justify-center">
                          <span className="text-white text-sm font-semibold">{currentData.imageCaptions[index]}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-6">
                  <p className="text-gray-300 leading-relaxed text-lg">
                    {currentData.description}
                  </p>
                  
                  <div className="border-l-4 border-[#c49a6c] pl-4">
                    <p className="text-[#c49a6c] font-semibold">
                      {activeYear === '1941' && "Where our story began..."}
                      {activeYear === '1988' && "Embracing change while honoring tradition..."}
                      {activeYear === '1999' && "Growing with our community..."}
                      {activeYear === '2024' && "A new chapter in luxury..."}
                    </p>
                  </div>
                  <div className="bg-gray-900/50 rounded-lg p-4">
                    <h4 className="font-semibold text-[#c49a6c] mb-2 flex items-center gap-2">
                      <Sparkles size={16} />
                      Key Highlights
                    </h4>
                    <ul className="space-y-2 text-gray-400 text-sm">
                      {activeYear === '1941' && (
                        <>
                          <li className="flex items-center gap-2">
                            <ChevronRight size={14} className="text-[#c49a6c]" />
                            Founded on principles of trust and quality
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight size={14} className="text-[#c49a6c]" />
                            Started as a small family-owned shop
                          </li>
                        </>
                      )}
                      {activeYear === '1988' && (
                        <>
                          <li className="flex items-center gap-2">
                            <ChevronRight size={14} className="text-[#c49a6c]" />
                            Major renovation and expansion
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight size={14} className="text-[#c49a6c]" />
                            Introduced new collection styles
                          </li>
                        </>
                      )}
                      {activeYear === '1999' && (
                        <>
                          <li className="flex items-center gap-2">
                            <ChevronRight size={14} className="text-[#c49a6c]" />
                            Became a household name
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight size={14} className="text-[#c49a6c]" />
                            Expanded collection offerings
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight size={14} className="text-[#c49a6c]" />
                            Modernized facilities
                          </li>
                        </>
                      )}
                      {activeYear === '2024' && (
                        <>
                          <li className="flex items-center gap-2">
                            <ChevronRight size={14} className="text-[#c49a6c]" />
                            Launched modern flagship store
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight size={14} className="text-[#c49a6c]" />
                            Ultimate luxury experience
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight size={14} className="text-[#c49a6c]" />
                            Exquisite craftsmanship showcase
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-12 text-center">
          <div className="inline-block bg-[#846358]/10 backdrop-blur-sm rounded-full px-6 py-3">
            <p className="text-gray-300">
              <span className="text-[#c49a6c] font-semibold">83+ Years</span> of Trust • Quality • Excellence
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransformationJourney;